import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { Box, Modal } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import useKdsAvailability from "../product/hooks/useKdsAvailability";
import { AccessModeKdsNavbar } from "../posnavbar/Navbar";
import "./kds.css";
import SideBarKDS from "./components/SideBar";
import LeftBarKds from "./components/LeftBar";
import KdsTableReference from "./components/KdsTableReference";
import { CheckIcon, GiftIcon } from "@heroicons/react/20/solid";
import EditItem from "./components/EditItem";
import {
  getToken,
  updateJobs,
  updateTaskStatus,
} from "../../../config/Services";
import KdsCompletedModal from "./components/KdsCompletedModal/KdsCompletedModal";
import PauseOnlineOrdersModal from "./components/PauseOnlineOrderModal";
import RecalledItemOrderModal from "./components/RecalledItemsModal";
import { PrinterIcon } from "@heroicons/react/24/outline";
import usePrint from "../product/hooks/usePrint";
import { useSnackbar } from "../product/hooks/SnackbarProvider";
import RedoAcceptModal from "./components/RedoAcceptModal";
import websiteLogo from "../../../assets/images/cutlery.png";
import zomato from "../../../assets/images/zomato.png";
import swiggy from "../../../assets/images/swiggy.png";
import whatsapp from "../../../assets/images/whatsapp.png";
import uberEats from "../../../assets/images/uberEats.jpg";
import MenuStockModal from "./components/MenuStock";
import InventoryStockModal from "./components/InventoryStock";
import { ChevronRightIcon } from "@heroicons/react/24/solid";
import KdsSettingsModal from "./components/KdsSettingsModal";
import PosCustomIcon from "../../../customComponents/PosCustomIcon/PosCustomIcon";
import SlotList from "../product/all-products/components/SlotItem/SlotList";
import OdsDateFilter from "../delivery/components/OdsDateFilter/OdsDateFilter";
import { refreshSlotCounts } from "../../../utils/odsArchitecture/odsSlotService";
import ItemWiseView, { ItemWorkloadStatus } from "./components/ItemWiseView";
import useItemWorkload from "./useItemWorkload";
import useKdsItemAccept from "./useKdsItemAccept";
import useKdsTableActions from "./useKdsTableActions";
import TimedOrderCard, { KitchenClockProvider, OrderCountdown } from "./components/TimedOrderCard";
import { formatPrepEstimate, formatReceivedTime } from "./orderTiming";
import { mapKitchenJob } from "./itemWorkload";
import { buildTableWorkload } from "./tableWorkload";
import useTableCatalogue from "./useTableCatalogue";
import useKitchenWorkloadActions from "./useKitchenWorkloadActions";
import TableWorkloadView from "./components/TableWorkloadView";
import { TableWorkloadSummary } from "./components/TableWiseView";
import TableOrderDetails, { PreparationCommentEditor } from "./components/TableOrderDetails";
import RejectOrderConfirmation from "./components/EditItem/RejectOrderConfirmation";
import { useKitchenNotifications } from "../../../context/KitchenNotificationsContext";
import useKitchenPendingActions from "./useKitchenPendingActions";
import { assertKitchenResponse, isLiveTask } from "./kitchenActions";
import { isPendingKitchenJob } from "./pendingKitchenDecisions";
import { filterSortKitchenOrders } from "./orderPresentation";
import { sortItemsByDue } from "./itemPreparation";
import ItemPreparation, { ServingGroupHeading } from "./components/ItemPreparation";
import RushOrdersBoard from "./components/RushOrdersBoard";
import useRushCompletedTickets from "./useRushCompletedTickets";
import RushPreparationSummary from "./components/RushPreparationSummary";
import { getRushPreparationGroups, getRushProcessingOrders } from "./rushWorkload";
import ProcessingSortMenu from "./components/ProcessingSortMenu";
import KdsSelectMenu from "./components/KdsSelectMenu";

// Current sizes are Medium; Small/Large shift every KDS font size by 2px.
const FONT_SIZE_DELTA = { small: "-2px", medium: "0px", large: "2px" };
const NO_TABLES = [];

const VIEW_OPTIONS = [
  { value: "orders", label: "Orders", detail: "Tickets as they come in" },
  { value: "items", label: "Items", detail: "Workload grouped by dish" },
  { value: "tables", label: "Tables", detail: "Orders grouped by table" },
];

const MODE_OPTIONS = [
  { value: "rush", label: "Rush mode", detail: "Prep totals for peak service" },
  { value: "expo", label: "Expo board", detail: "Full ticket board" },
];

// Platform configuration - easy to add new sales channels
const PLATFORM_CONFIG = {
  ZOMATO_ORDER: {
    icon: zomato,
    label: "Zomato",
    color: "#E23744",
  },
  SWIGGY_ORDER: {
    icon: swiggy,
    label: "Swiggy",
    color: "#FC8019",
  },
  WHATSAPP_ORDER: {
    icon: whatsapp,
    label: "WhatsApp",
    color: "#25D366",
  },
  UBER_EATS_ORDER: {
    icon: uberEats,
    label: "Uber Eats",
    color: "#000000",
  },
  B2C_ORDER: {
    icon: websiteLogo,
    label: "Website Order",
    color: "#0860E6",
  },
  POS: {
    icon: websiteLogo,
    label: "POS",
    color: "#374151",
  },
};

// Helper to check if order is from external platform (not POS)
const isExternalOrder = (type) => {
  return [
    "ZOMATO_ORDER",
    "SWIGGY_ORDER",
    "WHATSAPP_ORDER",
    "UBER_EATS_ORDER",
    "B2C_ORDER",
  ].includes(type);
};

// Sub order type display mapping
const SUB_ORDER_TYPE_MAP = {
  DINE_IN: "Dine In",
  TAKE_AWAY: "Take Away",
  DELIVERY: "Delivery",
  WHATSAPP_DINE_IN: "Dine In",
};

// PlatformBadge component
const PlatformBadge = ({ orderType, subOrderType }) => {
  const config = PLATFORM_CONFIG[orderType] || PLATFORM_CONFIG.POS;
  const displaySubOrderType = SUB_ORDER_TYPE_MAP[subOrderType] || subOrderType;

  return (
    <div className="platform-badge" style={{ color: config.color }}>
      <img src={config?.icon} alt={config?.label} className="platform-icon" />
      <span>{config?.label}</span>
    </div>
  );
};

// Stable defaults keep response-driven renders from restarting the fetch effects.
const EMPTY_FILTERS = Object.freeze([]);

const KitchenDisplayScreen = ({
  selectedSources = EMPTY_FILTERS,
  selectedPrepStations = EMPTY_FILTERS,
}) => {
  const locId = localStorage.getItem("locId");
  const orgId = localStorage.getItem("orgId");
  const branchSubType = localStorage.getItem("branchSubType");
  const tablesDisabled = branchSubType === "QSR";
  const retailUserDetails = JSON.parse(localStorage.getItem("retailUserDetails") || "{}");
  const customerName = JSON.parse(localStorage.getItem("customer_details"))?.name;
  const appConfig = JSON.parse(localStorage.getItem("appConfig")) || {};
  const orderBumpingMode = appConfig?.settings?.ORDER_BUMPING_MODE?.orderBumpingMode;
  const approveOrders = appConfig?.all?.APPROVE_ORDERS?.flag;
  const allowRejectOrders = appConfig?.all?.REJECT_ORDERS?.flag !== false
    && appConfig?.enabledFeatures?.REJECT_ORDERS !== false;
  const enabledDisplayModes = appConfig?.settings?.DISPLAY_SYSTEM_MODE?.kdsDisplayScreens;

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openCompletedModal, setOpenCompletedModal] = useState(false);
  const [buttonLoading, setButtonLoading] = useState({});
  const [kotPrintLoading, setKotPrintLoading] = useState({});
  // jobId → taskIds currently being marked ready, so only those items show a spinner
  const [savingTaskIds, setSavingTaskIds] = useState({});
  const [complimentaryKotPrinted, setComplimentaryKotPrinted] = useState(() => {
    try {
      const stored = localStorage.getItem("complimentaryKotPrintedState");
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  const [openMenuStockModal, setOpenMenuStockModal] = useState(false);
  const [openInventoryStockModal, setOpenInventoryModalStock] = useState(false);
  const [openSettingsModal, setOpenSettingsModal] = useState(false);

  // Date filter for the Processing column — null ⇒ today (the picker's Clear resets back to null)
  const [selectedDate, setSelectedDate] = useState(null);
  const [processingSort, setProcessingSort] = useState("oldest");
  const [pendingCollapsed, setPendingCollapsed] = useState(false);


  const [selectedItems, setSelectedItems] = useState({});
  const [confirmedReadyItems, setConfirmedReadyItems] = useState({});
  const readyRequestsRef = useRef(new Set());
  const confirmedReadyRef = useRef(new Set());
  const [fontSize, setFontSize] = useState("medium");
  const [openPauseOrders, setOpenPauseOrders] = useState(false);
  const [openRecalledItems, setOpenRecalledItems] = useState(false);
  const [openRedoAccept, setOpenRedoAccept] = useState(false);

  // Add search state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDisplayView, setDisplayView] = useState("orders");
  const displayView = tablesDisabled && selectedDisplayView === "tables" ? "orders" : selectedDisplayView;
  const [rushMode, setRushMode] = useState(true);
  const [rushCheckedItems, setRushCheckedItems] = useState({});
  const rushCompletedTickets = useRushCompletedTickets(JSON.stringify([
    orgId, locId, selectedDate, selectedSources, selectedPrepStations, searchQuery,
  ]));
  const [selectedQueueKey, setSelectedQueueKey] = useState(null);
  const [slotsHeaderTarget, setSlotsHeaderTarget] = useState();
  const [tableFilter, setTableFilter] = useState("active");
  // Active tables come purely from KdsAggsviews; the layout catalogue is only for "All tables".
  const allTablesView = displayView === "tables" && tableFilter === "all";
  const tableCatalogue = useTableCatalogue({
    enabled: allTablesView,
    orgId, locId, layoutId: localStorage.getItem("layoutId"),
  });
  const catalogueTables = allTablesView ? tableCatalogue.tables : NO_TABLES;
  const catalogueLoading = allTablesView && tableCatalogue.loading;
  const catalogueError = allTablesView ? tableCatalogue.error : "";
  const itemWorkload = useItemWorkload({
    enabled: true,
    orgId,
    locId,
    query: searchQuery,
    selectedDate,
    selectedSources,
    selectedPrepStations,
    aggregationType: displayView === "orders" ? null : displayView === "tables" ? "TABLE" : "ITEM",
    sort: displayView === "orders" ? processingSort : null,
  });
  const [selectedTableKey, setSelectedTableKey] = useState(null);
  const [detailJobId, setDetailJobId] = useState(null);
  const orderAlert = useKitchenNotifications();
  const pendingActions = useKitchenPendingActions({
    getFreshJobs: itemWorkload.getFreshJobs,
    onUpdated: itemWorkload.applyPendingDecision,
    onAcknowledged: orderAlert.acknowledge,
    orgId,
    locationId: locId,
    updatedBy: retailUserDetails.uidx || "",
    updatedByName: customerName || "",
    allowAccept: approveOrders !== false,
    allowReject: allowRejectOrders,
    appConfig,
  });
  const workloadActions = useKitchenWorkloadActions({
    locationId: locId,
    updatedBy: retailUserDetails.uidx || "",
    updatedByName: customerName || "",
    getFreshJobs: itemWorkload.getFreshJobs,
    onUpdated: () => refreshBothAfterAction(),
  });
  const itemAccept = useKdsItemAccept({
    enabled: displayView === "items",
    allowAccept: approveOrders !== false,
    groups: itemWorkload.groups,
    locationId: locId,
    updatedBy: retailUserDetails.uidx || "",
    updatedByName: customerName || "",
    onUpdated: itemWorkload.refresh,
    onAcknowledged: orderAlert.acknowledge,
  });
  const tableWorkload = React.useMemo(
    () => buildTableWorkload(catalogueTables, itemWorkload.jobs),
    [catalogueTables, itemWorkload.jobs],
  );
  const tableActions = useKdsTableActions({
    enabled: displayView === "tables",
    tables: tableWorkload,
    locationId: locId,
    updatedBy: retailUserDetails.uidx || "",
    updatedByName: customerName || "",
    allowAccept: approveOrders !== false,
    allowReject: allowRejectOrders,
    onUpdated: itemWorkload.refresh,
    onAcknowledged: orderAlert.acknowledge,
  });
  const sharedOrders = useMemo(
    () => (itemWorkload.jobs || []).map((job) => ({
      ...mapKitchenJob(job),
      pendingEligible: isPendingKitchenJob(job),
      orderSequence: job?.orderSequence ?? job?.tasks?.[0]?.metadata?.placedSequence,
    })),
    [itemWorkload.jobs],
  );
  const pendingOrders = sharedOrders.filter((order) => order.pendingEligible);
  const activeOrders = sharedOrders.filter((order) => order.status === "IN_PROGRESS" || order.status === "ACCEPTED");
  const displayedActiveOrders = useMemo(
    () => filterSortKitchenOrders(activeOrders, {
      sort: processingSort,
      now: Date.now(),
    }),
    [activeOrders, processingSort],
  );
  const rushProcessingOrders = useMemo(
    () => getRushProcessingOrders(activeOrders, confirmedReadyItems, selectedPrepStations, rushCompletedTickets.orders),
    [activeOrders, confirmedReadyItems, selectedPrepStations, rushCompletedTickets.orders],
  );
  const displayedRushOrders = useMemo(
    () => filterSortKitchenOrders(rushProcessingOrders, { sort: processingSort }),
    [rushProcessingOrders, processingSort],
  );
  const rushOrdinalById = useMemo(
    () => Object.fromEntries(rushProcessingOrders.map((order, index) => [order.id, index + 1])),
    [rushProcessingOrders],
  );
  const rushPreparationGroups = useMemo(
    () => getRushPreparationGroups(itemWorkload.jobs, confirmedReadyItems, selectedPrepStations),
    [itemWorkload.jobs, confirmedReadyItems, selectedPrepStations],
  );
  const selectedQueueGroup = rushPreparationGroups.find((group) => group.key === selectedQueueKey);
  const highlightedRushIds = selectedQueueGroup?.orderIds || [];
  // First fetch for the current view/filters only — background polling must not flicker the toggles.
  const viewLoading = (itemWorkload.loading && !itemWorkload.lastUpdated && !itemWorkload.error) ||
    catalogueLoading;
  useEffect(() => {
    setRushCheckedItems({});
    setSelectedQueueKey(null);
  }, [orgId, locId]);
  useEffect(() => {
    if (!itemWorkload.loading && selectedQueueKey && !selectedQueueGroup) setSelectedQueueKey(null);
  }, [itemWorkload.loading, selectedQueueKey, selectedQueueGroup]);
  const detailTable = detailJobId
    ? { key: `job:${detailJobId}`, name: null, jobs: itemWorkload.jobs.filter((job) => job.jobId === detailJobId) }
    : tableWorkload.find((table) => table.key === selectedTableKey);
  const detailsOpen = !!(selectedTableKey || detailJobId);
  const detailJobsKey = JSON.stringify((detailTable?.jobs || []).map((job) => [job.jobId, job.metadata?.cartId]));
  useEffect(() => {
    if (detailsOpen) workloadActions.loadNotes(detailTable?.jobs || []);
  }, [detailsOpen, detailJobsKey, workloadActions.loadNotes]);

  const kdsRootRef = useRef(null);


  // Track auto-bump timers per pending order (jobId → timerId)
  const autoBumpTimersRef = useRef({});

  const { printToken } = usePrint();
  const showSnackbar = useSnackbar();

  useEffect(() => {
    setSlotsHeaderTarget(document.getElementById("kds-slots-header-mount"));
  }, []);

  /** Load saved font size */
  useEffect(() => {
    const saved = localStorage.getItem("kdsFontSize");
    if (saved) setFontSize(saved);
  }, []);

  const handleFontChange = (size) => {
    setFontSize(size);
    localStorage.setItem("kdsFontSize", size);
  };

  // Set on <html> so drawers and menus portaled outside the KDS root resize too.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--kds-font-delta", FONT_SIZE_DELTA[fontSize] || "0px");
    return () => root.style.removeProperty("--kds-font-delta");
  }, [fontSize]);

  /** Item selection handler */
  const handleItemSelect = (orderId, itemId, isChecked) => {
    setSelectedItems((prev) => {
      const prevSelected = prev[orderId] || [];
      if (isChecked) {
        return { ...prev, [orderId]: [...prevSelected, itemId] };
      } else {
        return { ...prev, [orderId]: prevSelected.filter((id) => id !== itemId) };
      }
    });
  };

  const runSearch = useCallback(
    (query) => setSearchQuery(query),
    []
  );

  /** Handle search change with debounce */
  const handleSearchChange = useCallback(
    (query) => {
      runSearch(query);
    },
    [runSearch]
  );

  useEffect(() => {
    const handleGlobalKdsSearch = (event) => {
      handleSearchChange(event?.detail?.query || "");
    };

    window.addEventListener("pos:kds-search", handleGlobalKdsSearch);
    return () => {
      window.removeEventListener("pos:kds-search", handleGlobalKdsSearch);
    };
  }, [handleSearchChange]);

  /** Auto-bump pending orders to IN_PROGRESS after 10 seconds when AUTO mode is on */
  useEffect(() => {
    if (orderBumpingMode !== "AUTO") return;

    const currentPendingIds = new Set(pendingOrders.map((o) => o.id));

    // Clear timers for orders no longer in the pending list
    Object.keys(autoBumpTimersRef.current).forEach((jobId) => {
      if (!currentPendingIds.has(jobId)) {
        clearTimeout(autoBumpTimersRef.current[jobId]);
        delete autoBumpTimersRef.current[jobId];
      }
    });

    // Schedule a new timer only for orders that don't already have one
    pendingOrders.forEach((order) => {
      if (!autoBumpTimersRef.current[order?.id]) {
        autoBumpTimersRef.current[order?.id] = setTimeout(() => {
          delete autoBumpTimersRef.current[order?.id];
          handleProcessing(order?.id);
        }, 10000);
      }
    });
  }, [pendingOrders, orderBumpingMode]);

  /** Cleanup all auto-bump timers on unmount */
  useEffect(() => {
    return () => {
      Object.values(autoBumpTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  /** Refresh both after actions */
  const refreshBothAfterAction = async () => {
    await itemWorkload.refresh();
  };

  const handleOrderReversed = async (jobId) => {
    // Reversal starts a new preparation cycle for the same job/task IDs.
    (confirmedReadyItems[jobId] || []).forEach((taskId) => {
      confirmedReadyRef.current.delete(JSON.stringify([jobId, taskId]));
    });
    const clearJob = (previous) => {
      const next = { ...previous };
      delete next[jobId];
      return next;
    };
    setConfirmedReadyItems(clearJob);
    setSelectedItems(clearJob);
    setRushCheckedItems(clearJob);
    rushCompletedTickets.forget(jobId);
    try {
      await refreshBothAfterAction();
    } catch {
      showSnackbar("Order reversed, but the board could not refresh. Please refresh the board.", "warning");
    }
  };

  /** Global refresh from the top navbar */
  useEffect(() => {
    const handleGlobalKdsRefresh = () => {
      itemWorkload.refresh();
      if (allTablesView) tableCatalogue.refresh();
      refreshSlotCounts();
    };

    window.addEventListener("pos:kds-refresh", handleGlobalKdsRefresh);
    return () => {
      window.removeEventListener("pos:kds-refresh", handleGlobalKdsRefresh);
    };
  }, [itemWorkload.refresh, tableCatalogue.refresh, allTablesView]);

  /** Start / Ready Handlers */
  /** Start / Ready Handlers */
  const handleProcessing = async (jobId) => {
    try {
      setButtonLoading((prev) => ({ ...prev, [jobId]: "processing" }));
      const payload = {
        jobIds: [jobId],
        status: "IN_PROGRESS",
        locationId: locId,
        updatedBy: retailUserDetails.uidx || "",
        updatedByName: customerName || "",
      };
      const response = await updateJobs(payload);
      assertKitchenResponse(response);

      // Show success snackbar
      showSnackbar("Order moved to In Progress", "success");

      orderAlert.acknowledge([jobId]);
      await itemWorkload.applyPendingDecision({ jobIds: [jobId], status: "IN_PROGRESS" });
    } catch (error) {
      // Show error snackbar
      showSnackbar("Failed to update order status", "error");
    } finally {
      setButtonLoading((prev) => ({ ...prev, [jobId]: null }));
    }
  };

  const toggleRushItem = (jobId, taskId) => handleReady(jobId, [taskId], true);

  const bumpRushOrder = async (order) => {
    if (readyRequestsRef.current.has(order.id) || buttonLoading[order.id]) return;
    const success = await handleReady(order.id, order.items.map((item) => item.taskId));
    if (success) {
      rushCompletedTickets.forget(order.id);
      setRushCheckedItems((previous) => {
        const next = { ...previous };
        delete next[order.id];
        return next;
      });
    }
  };

  const handleReady = async (jobId, explicitTaskIds, retainRushTicket = false) => {
    if (readyRequestsRef.current.has(jobId) || buttonLoading[jobId]) return false;
    const order = activeOrders.find((candidate) => candidate.id === jobId);
    const selected = [...new Set(explicitTaskIds || selectedItems[jobId] || [])].filter((taskId) =>
      taskId != null && !confirmedReadyRef.current.has(JSON.stringify([jobId, taskId])) &&
      order?.items?.some((item) => item.taskId === taskId && item.status === "IN_PROGRESS" && isLiveTask(item))
    );
    if (selected.length === 0) return false;
    readyRequestsRef.current.add(jobId);
    try {
      setButtonLoading((prev) => ({ ...prev, [jobId]: "ready" }));
      setSavingTaskIds((prev) => ({ ...prev, [jobId]: selected }));
      const payload = {
        jobId: jobId,
        taskIds: selected,
        status: "COMPLETED",
        locationId: locId,
        updatedBy: retailUserDetails.uidx || "",
        updatedByName: customerName || "",
      };
      assertKitchenResponse(await updateTaskStatus(payload));
      selected.forEach((taskId) => confirmedReadyRef.current.add(JSON.stringify([jobId, taskId])));
      setConfirmedReadyItems((prev) => ({
        ...prev, [jobId]: [...new Set([...(prev[jobId] || []), ...selected])],
      }));
      if (retainRushTicket) {
        const hasRemainingItems = order.items.some((item) => isLiveTask(item) &&
          item?.status !== "READY" && item?.taskStatus !== "READY" &&
          !confirmedReadyRef.current.has(JSON.stringify([jobId, item?.taskId])));
        if (hasRemainingItems) rushCompletedTickets.remember(order, selected);
        else rushCompletedTickets.forget(jobId);
      }

      // Show success snackbar
      showSnackbar(
        `${selected.length} item${selected.length > 1 ? "s" : ""} marked as ready`,
        "success"
      );

      setSelectedItems((prev) => ({
        ...prev, [jobId]: (prev[jobId] || []).filter((taskId) => !selected.includes(taskId)),
      }));
      try {
        await refreshBothAfterAction();
      } catch (_) {
        showSnackbar("Items are ready, but the board could not refresh. Please refresh the board.", "warning");
      }
      return true;
    } catch (error) {
      // Show error snackbar
      showSnackbar("Failed to mark items as ready", "error");
      return false;
    } finally {
      readyRequestsRef.current.delete(jobId);
      setButtonLoading((prev) => ({ ...prev, [jobId]: null }));
      setSavingTaskIds((prev) => {
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
    }
  };

  const handleCompletedOption = () => setOpenCompletedModal(true);

  const handleSelectAllItems = (orderId, items, isChecked) => {
    setSelectedItems((prev) => {
      if (isChecked) {
        const inProgressIds = items
          .filter(
            (item) =>
              item?.status === "IN_PROGRESS" &&
              item?.taskStatus !== "COMPLETED" &&
              item?.taskStatus !== "REMOVED"
          )
          .map((item) => item?.taskId);
        return { ...prev, [orderId]: inProgressIds };
      } else {
        return { ...prev, [orderId]: [] };
      }
    });
  };

  const handlePauseOrders = () => {
    setOpenPauseOrders(true);
  };

  const handleClosePause = () => {
    setOpenPauseOrders(false);
  };

  const handleRecalledItems = () => {
    setOpenRecalledItems(true);
  };

  const handleRedoAccept = () => {
    setOpenRedoAccept(true);
  };

  const handleRedoAcceptClose = async () => {
    setOpenRedoAccept(false);
    await refreshBothAfterAction();
  };

  const handleCompletedClose = async () => {
    await refreshBothAfterAction();
  };

  const handleOpenMenuStock = () => {
    setOpenMenuStockModal(true);
  };

  const handleMenuStocksClose = () => {
    setOpenMenuStockModal(false);
  };

  const handleOpenInventoryStock = () => {
    setOpenInventoryModalStock(true);
  };

  const handleMenuInventoryClose = () => {
    setOpenInventoryModalStock(false);
  };


  const handlePrintKOT = async (order) => {
    if (!enabledDisplayModes.find((mode) => mode === "KOT")) {
      showSnackbar("KOT Printing is disabled from configuration", "warning");
      return;
    }
    const SEND_TO_KITCHEN_KEY = "sendToKitchen";
    let sendToKitchenEnabled = false;
    try {
      sendToKitchenEnabled = localStorage.getItem(SEND_TO_KITCHEN_KEY) === "true";
    } catch {
      sendToKitchenEnabled = false;
    }

    // Determine if sendToKitchen should be included
    const shouldSendToKitchen = branchSubType === "FINE_DINE" && sendToKitchenEnabled;

    try {
      setKotPrintLoading((prev) => ({ ...prev, [order?.id]: true }));
      const tokenPayload = {
        cartId: order?.cartId,
        orderSequence: order?.orderSequence,
        generatedBy: retailUserDetails.uidx || "",
        generatedByName: retailUserDetails.firstName,
        ...(shouldSendToKitchen && { isSendToKitchen: true }),
        // externalId: order?.externalRefId,
        // locationId: locId,
        // reopenedBy: retailUserDetails?.uidx || "",
        // reopenedByName: retailUserDetails?.firstName || "",
      };

      const res = await getToken(tokenPayload);
      if (res?.data?.data?.es > 0) {
        showSnackbar(res?.data?.data?.message || "There was an error in printing the KOT", "error");
        return;
      }

      if (res?.data?.data?.data && !shouldSendToKitchen) {
        await printToken({
          tableId: res?.data?.data?.data?.tableId,
          tokenNumber: res?.data?.data?.data?.tokenNumber,
          tableNumber: res?.data?.data?.data?.tableNumber,
          subOrderType: res?.data?.data?.data?.subOrderType,
          customerName: customerName || "Walk-in Customer",
          orderItems: res?.data?.data?.data?.orderedProducts,
          comments: res?.data?.data?.data?.comments || "N/A",
          layoutName: res?.data?.data?.data?.layoutName,
        });
      }

      // Stop the complimentary blink once the KOT has been successfully reprinted
      setComplimentaryKotPrinted((prev) => {
        const newState = { ...prev, [order?.id]: true };
        const keys = Object.keys(newState);
        if (keys.length > 200) {
          delete newState[keys[0]]; // Remove oldest entry to prevent localStorage bloat
        }
        localStorage.setItem("complimentaryKotPrintedState", JSON.stringify(newState));
        return newState;
      });
    } catch (error) {
    } finally {
      setKotPrintLoading((prev) => ({ ...prev, [order?.id]: false }));
    }
  };

  useEffect(() => {
    const root = kdsRootRef.current;
    if (!root) return;

    const canScroll = (el, deltaX, deltaY) => {
      if (!el) return false;

      const style = window.getComputedStyle(el);
      const canScrollX =
        (style.overflowX === "auto" || style.overflowX === "scroll") &&
        el.scrollWidth > el.clientWidth;

      const canScrollY =
        (style.overflowY === "auto" || style.overflowY === "scroll") &&
        el.scrollHeight > el.clientHeight;

      if (deltaX < 0 && canScrollX && el.scrollLeft > 0) return true;
      if (deltaX > 0 && canScrollX && el.scrollLeft + el.clientWidth < el.scrollWidth) return true;

      if (deltaY < 0 && canScrollY && el.scrollTop > 0) return true;
      if (deltaY > 0 && canScrollY && el.scrollTop + el.clientHeight < el.scrollHeight) return true;

      return false;
    };

    const findScrollableParent = (el, deltaX, deltaY) => {
      let current = el;
      while (current && current !== root) {
        if (canScroll(current, deltaX, deltaY)) return true;
        current = current.parentElement;
      }
      return false;
    };

    const handleWheel = (e) => {
      if (root.classList.contains("kds-orders-screen") && !e.ctrlKey &&
        Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        const processingRail = e.target.closest?.(".kds-processing-rail");
        const processingScroller = processingRail?.closest(".kds-orders-rail-container");
        if (processingRail && processingScroller &&
          processingScroller.scrollWidth > processingScroller.clientWidth) {
          // Let an order's item list consume vertical scrolling before translating
          // the remaining mouse-wheel gesture into horizontal rail movement.
          if (e.target.closest?.("[role='dialog'], [role='menu'], .MuiPopover-root, .MuiModal-root, .MuiDrawer-root")) {
            return;
          }
          let child = e.target;
          while (child && child !== processingRail) {
            if (canScroll(child, 0, e.deltaY)) return;
            child = child.parentElement;
          }
          processingScroller.scrollLeft += e.deltaY;
          e.preventDefault();
          return;
        }

        const rail = e.target.closest?.(".left-bar-kds .orders-container");
        if (rail && rail.scrollWidth > rail.clientWidth) {
          if (canScroll(rail, 0, e.deltaY)) return;
          let child = e.target;
          while (child && child !== rail) {
            if (canScroll(child, 0, e.deltaY)) return;
            child = child.parentElement;
          }
          rail.scrollLeft += e.deltaY;
          e.preventDefault();
          return;
        }
      }
      const hasScrollableParent = findScrollableParent(e.target, e.deltaX, e.deltaY);

      //No container can consume this gesture → browser would navigate
      if (!hasScrollableParent) {
        e.preventDefault();
      }
    };

    root.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      root.removeEventListener("wheel", handleWheel);
    };
  }, []);

  return (
    <KitchenClockProvider>
    <div className={`kds-screen-layout font-${fontSize}${displayView === "orders" ? " kds-orders-screen" : ""}`} ref={kdsRootRef}>
      {slotsHeaderTarget === undefined ? null : slotsHeaderTarget
        ? createPortal(
          <div className="kds-slots-container kds-header-slots-container">
            <SlotList onSlotSelect={refreshBothAfterAction} />
          </div>,
          slotsHeaderTarget,
        )
        : (
          <div className="kds-slots-container">
            <SlotList onSlotSelect={refreshBothAfterAction} />
          </div>
        )}
      <div className={`kds-main-layout${displayView === "orders" ? " kds-orders-layout" : displayView === "tables" ? " kds-tables-layout" : ""}`}>
        {((displayView === "orders" && rushMode) || displayView === "tables") && (
          <RushPreparationSummary
            groups={rushPreparationGroups}
            selectedGroupKey={displayView === "orders" && rushMode ? selectedQueueKey : null}
            onSelectGroup={displayView === "orders" && rushMode
              ? (key) => setSelectedQueueKey((current) => current === key ? null : key)
              : undefined}
            pendingOrders={pendingOrders}
            loading={itemWorkload.loading}
            error={itemWorkload.error}
            onRetry={itemWorkload.refresh}
            onAcceptAll={() => pendingActions.accept(pendingOrders.map((order) => order.id))}
            actionBusy={pendingActions.busy}
            allowAccept={approveOrders !== false}
          />
        )}
        {displayView === "orders" && !rushMode && orderBumpingMode !== "AUTO" && (
          <LeftBarKds
            orders={pendingOrders}
            totalPendingResults={pendingOrders.length}
            pendingLoading={itemWorkload.loading}
            error={itemWorkload.error}
            onRetry={itemWorkload.refresh}
            onAcceptOrders={pendingActions.accept}
            onRejectOrders={pendingActions.requestReject}
            actionBusy={pendingActions.busy}
            allowAccept={approveOrders !== false}
            allowReject={allowRejectOrders}
            collapsed={pendingCollapsed}
            onToggle={() => setPendingCollapsed((collapsed) => !collapsed)}
          />
        )}

        <div className="kds-orders-container">
          <div className="kds-section-header">
            <div className={`kds-section-left${displayView === "tables" ? " kds-table-section-left" : ""}`}>
              {/* <div className="kds-section-dot"></div> */}
              <h2>{displayView === "items" ? "Kitchen workload" : displayView === "tables" ? "Tables" : "Processing"}</h2>
              <div className="count-badge">
                {displayView === "items" ? itemWorkload.groups.length : displayView === "tables" ? tableWorkload.length : rushMode ? rushProcessingOrders.length : displayedActiveOrders.length}
              </div>
              {displayView === "items" && (
                <ItemWorkloadStatus
                  loading={itemWorkload.loading}
                  error={itemWorkload.error}
                  lastUpdated={itemWorkload.lastUpdated}
                  hasData={itemWorkload.groups.length > 0}
                />
              )}
              {displayView === "tables" && (
                <TableWorkloadSummary
                  tables={tableWorkload}
                  loading={catalogueLoading || itemWorkload.loading}
                  error={catalogueError || itemWorkload.error}
                />
              )}
            </div>
            <div className="kds-section-right-group">
              {orderAlert.needsInteraction && (
                <button type="button" className="kds-enable-sound" onClick={orderAlert.enableSound}>
                  Enable sound alerts
                </button>
              )}
              <KdsSelectMenu
                value={displayView}
                options={VIEW_OPTIONS.map((option) => (option.value === "tables" && tablesDisabled
                  ? { ...option, unavailable: true, detail: "No tables for this branch" }
                  : option))}
                onChange={(view) => {
                  if (view === "tables" && tablesDisabled) {
                    showSnackbar("No Tables Found", "warning");
                    return;
                  }
                  setDisplayView(view); setSelectedTableKey(null); setDetailJobId(null);
                }}
                menuTitle="Kitchen display view"
                menuHint="Choose a view"
                ariaLabel="Kitchen display view"
                loading={viewLoading}
                disabled={workloadActions.busy || pendingActions.busy || itemAccept.busy || tableActions.busy}
              />
              {displayView === "orders" && (
                <KdsSelectMenu
                  value={rushMode ? "rush" : "expo"}
                  options={MODE_OPTIONS}
                  onChange={(mode) => setRushMode(mode === "rush")}
                  menuTitle="Orders display mode"
                  menuHint="Choose a layout"
                  ariaLabel="Orders display mode"
                />
              )}
              {displayView === "orders" && (
                <ProcessingSortMenu value={processingSort} onChange={setProcessingSort} />
              )}
              <div className="kds-date-container" data-testid="text-kds-current-date">
                <OdsDateFilter selectedDate={selectedDate} onChange={(d) => setSelectedDate(d)} />
              </div>
            </div>
          </div>

          {itemWorkload.loading && (
            <div className="horizontal-loader" role="status" aria-live="polite"
              aria-label="Loading kitchen orders">
              <div className="horizontal-loader-bar" aria-hidden="true"></div>
            </div>
          )}
          <div
            className={`kds-main-order-container${displayView === "orders" ? " kds-orders-rail-container" : ""}${displayView === "orders" && rushMode ? " kds-rush-mode" : ""}`}
            tabIndex={displayView === "orders" ? 0 : undefined}
            role={displayView === "orders" ? "region" : undefined}
            aria-label={displayView === "orders" ? "Processing orders" : undefined}
          >
            {displayView === "items" ? (
              <ItemWiseView
                groups={itemWorkload.groups}
                loading={itemWorkload.loading}
                error={itemWorkload.error}
                lastUpdated={itemWorkload.lastUpdated}
                onRetry={itemWorkload.refresh}
                onReady={itemAccept.ready}
                onReadyQuantity={itemAccept.readyQuantity}
                aggregatedReady
                onAccept={itemAccept.accept}
                allowAccept={approveOrders !== false}
                allowReject={allowRejectOrders}
                onOpenOrder={setDetailJobId}
                actionBusy={workloadActions.busy || itemAccept.busy}
              />
            ) : displayView === "tables" ? (
              <TableWorkloadView
                catalogue={catalogueTables}
                jobs={itemWorkload.jobs}
                filter={tableFilter}
                onFilterChange={setTableFilter}
                loading={catalogueLoading || itemWorkload.loading}
                error={catalogueError || itemWorkload.error}
                onRetry={() => { if (allTablesView) tableCatalogue.refresh(); itemWorkload.refresh(); }}
                onOpenTable={setSelectedTableKey}
                onAcceptOrders={tableActions.accept}
                onRejectOrders={tableActions.reject}
                actionBusy={tableActions.busy}
                activeAction={tableActions.action}
                allowAccept={approveOrders !== false}
                allowReject={allowRejectOrders}
              />
            ) : rushMode ? (
              <RushOrdersBoard
                orders={displayedRushOrders}
                ordinalById={rushOrdinalById}
                confirmedReadyItems={confirmedReadyItems}
                buttonLoading={buttonLoading}
                savingTaskIds={savingTaskIds}
                checkedItems={rushCheckedItems}
                onToggleItem={toggleRushItem}
                onBumpOrder={bumpRushOrder}
                highlightedOrderIds={highlightedRushIds}
                highlightKey={selectedQueueGroup?.key || null}
                onEdit={setSelectedOrder}
                loading={itemWorkload.loading}
                error={itemWorkload.error}
                onRetry={itemWorkload.refresh}
              />
            ) : (
              <>
            {itemWorkload.error && (
              <div className="kds-workload-error" role="alert">
                <span>{itemWorkload.error}</span>
                <button type="button" onClick={itemWorkload.refresh} disabled={itemWorkload.loading}
                  aria-busy={itemWorkload.loading}>
                  {itemWorkload.loading && <span className="loader kds-btn-loader" aria-hidden="true" />}
                  {itemWorkload.loading ? "Retrying…" : "Retry orders"}
                </button>
              </div>
            )}
            {activeOrders.length === 0 && !itemWorkload.loading && !itemWorkload.error && (
              <p className="no-orders-text">No active orders yet.</p>
            )}
            {(() => {
              const cards = [];
              (Array.isArray(displayedActiveOrders) ? displayedActiveOrders : []).forEach((order) => {
                // Filter items by selected prep station first
                const stationFilteredItems =
                  selectedPrepStations?.length > 0
                    ? order?.items?.filter((item) =>
                        selectedPrepStations?.includes(item?.metadata?.prepStationId)
                      ) || []
                    : order?.items || [];
                const preparationSortedItems = sortItemsByDue(order, stationFilteredItems);

                // Check if there are any visible items (not COMPLETED and not REMOVED)
                const visibleItems =
                  stationFilteredItems?.filter((item) => item?.taskStatus !== "COMPLETED" && item?.taskStatus !== "REMOVED") || [];

                // Don't process the card if there are no visible items
                if (visibleItems?.length === 0) return;

                cards.push({ order, stationFilteredItems: preparationSortedItems });
              });

              return (
                <div className="kds-processing-rail">
                  {cards.map(({ order, stationFilteredItems }) => (
                        <TimedOrderCard key={order?.id} order={order} className="kds-order-card">
                          {/* header */}
                          <section className="kds-order-card-header">
                            {/* Row 1: Order No & Edit button on left, Creation time & Prep time on right */}
                            <div className="kds-ref-header-row-1">
                              <div className="kds-ref-header-left">
                                <p className="kds-ref-order-no">{order?.orderNo ?? "-"}</p>
                                <button
                                  type="button"
                                  className="kds-ref-edit-btn"
                                  data-testid={`button-edit-kds-${order?.id}`}
                                  onClick={() => setSelectedOrder(order)}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="lucide lucide-pencil h-4 w-4"
                                    aria-hidden="true"
                                  >
                                    <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"></path>
                                    <path d="m15 5 4 4"></path>
                                  </svg>
                                </button>
                              </div>
                              <div className="kds-ref-header-right">
                                <div className="kds-ref-time-prep-group">
                                  <span>
                                    {formatReceivedTime(order?.created)}
                                  </span>
                                  <span className="kds-ref-prep-tag">
                                    Prep: {formatPrepEstimate(order?.prepTime)}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <OrderCountdown order={order} showDescription={false} />

                            {/* Row 2: Platform on left, Token / SubOrderType on right */}
                            <div className="kds-ref-header-row-2">
                              <div className="kds-ref-platform-left">
                                <PlatformBadge
                                  orderType={order?.orderType ?? ""}
                                  subOrderType={order?.metadata?.subOrderType ?? ""}
                                />
                              </div>
                              <div className="kds-ref-info-right">
                                <span className="kds-ref-kot-token">
                                  KOT #{order?.token || order?.id || "-"}
                                </span>
                                <span className="kds-ref-divider">|</span>
                                <span className="kds-ref-sub-order-type">
                                  {SUB_ORDER_TYPE_MAP[order?.metadata?.subOrderType] ||
                                    order?.metadata?.subOrderType ||
                                    "-"}
                                </span>
                              </div>
                            </div>

                            {/* Row 3: Customer Name & optional Table Info */}
                            <div className="kds-ref-header-row-3">
                              <p className="kds-ref-customer-name">
                                {order?.metadata?.userName ?? "NA"}
                              </p>
                              {!isExternalOrder(order?.orderType) &&
                                branchSubType === "FINE_DINE" && (
                                  <div className="kds-ref-table-info">
                                    <KdsTableReference
                                      tableNumber={order?.metadata?.tableNumber}
                                      layoutName={order?.metadata?.layoutName}
                                    />
                                  </div>
                                )}
                            </div>
                          </section>

                          <div className="kds-order-body">
                            {(() => {
                              // Filter items that are IN_PROGRESS and NOT completed or removed
                              const inProgressItems = stationFilteredItems?.filter(
                                (item) =>
                                  item?.status === "IN_PROGRESS" &&
                                  item?.taskStatus !== "COMPLETED" &&
                                  item?.taskStatus !== "REMOVED"
                              );

                              // Only show "Select All" checkbox when there are 2 or more in-progress items
                              const showSelectAll = inProgressItems.length > 1 && !rushMode;

                              // Only render Select All if there are in-progress items
                              if (inProgressItems.length > 0) {
                                return (
                                  <>
                                    {showSelectAll && (
                                      <div className="select-all-bar">
                                        <label className="select-all-label">
                                          <input
                                            id={`selectAll-${order?.id}`}
                                            type="checkbox"
                                            className="select-all-checkbox"
                                            checked={
                                              selectedItems[order?.id]?.length ===
                                                inProgressItems?.length &&
                                              inProgressItems?.length > 0
                                            }
                                            onChange={(e) =>
                                              handleSelectAllItems(
                                                order?.id,
                                                stationFilteredItems,
                                                e.target.checked
                                              )
                                            }
                                          />
                                          <span className="item-name">{"All"}</span>
                                        </label>
                                        <span className="select-all-count">
                                          {`${selectedItems[order?.id]?.length || 0}/${
                                            inProgressItems?.length
                                          } selected`}
                                        </span>
                                      </div>
                                    )}

                                    {stationFilteredItems?.map((item, itemIndex) => (
                                      <div className="item-block" key={item?.taskId || item?.id}>
                                        <ServingGroupHeading order={order} item={item} items={stationFilteredItems} index={itemIndex} />
                                        {(() => {
                                          const isConfirmedReady = confirmedReadyItems?.[order?.id]?.includes(item?.taskId);
                                          const isSavingItem = !!savingTaskIds[order?.id]?.includes(item?.taskId);
                                          const ItemLabel = rushMode ? "button" : "label";
                                          return (
                                        <div
                                          className={`item-line ${
                                            item?.taskStatus === "REMOVED" ? "removed-item" : ""
                                          }`}
                                        >
                                          <ItemLabel className={`item-checkbox${rushMode ? " kds-rush-item-label" : ""}`}
                                            {...(rushMode ? {
                                              type: "button",
                                              "aria-label": isConfirmedReady ? `${item?.name} ready` : `Mark ${item?.name} ready in order ${order?.token || order?.id}`,
                                              "aria-busy": isSavingItem,
                                              disabled: !!buttonLoading[order?.id] || isConfirmedReady || item?.taskId == null ||
                                                item?.status !== "IN_PROGRESS" || !isLiveTask(item),
                                              onClick: () => handleReady(order?.id, [item.taskId]),
                                            } : {})}>
                                            {/* Show checkbox only for IN_PROGRESS items that are NOT removed */}
                                            {!rushMode && item?.status === "IN_PROGRESS" &&
                                              item?.taskStatus !== "REMOVED" &&
                                              item?.taskStatus !== "COMPLETED" && (
                                                <input
                                                  type="checkbox"
                                                  checked={
                                                    item?.status === "COMPLETED"
                                                      ? true
                                                      : selectedItems[order?.id]?.includes(
                                                          item?.taskId
                                                        ) || false
                                                  }
                                                  disabled={item?.taskStatus === "COMPLETED"}
                                                  className={`${
                                                    item?.taskStatus === "COMPLETED"
                                                      ? "completed-checkbox"
                                                      : ""
                                                  }`}
                                                  onChange={(e) => {
                                                    if (item?.taskStatus === "COMPLETED") return;
                                                    handleItemSelect(
                                                      order?.id,
                                                      item?.taskId,
                                                      e.target.checked
                                                    );
                                                  }}
                                                />
                                              )}
                                            <span
                                                title={item?.name || "Unnamed item"}
                                              className={`item-name ${
                                                item?.taskStatus === "COMPLETED" || isConfirmedReady
                                                  ? "item-completed"
                                                  : ""
                                              }`}
                                            >
                                              {item?.taskStatus === "COMPLETED" && (
                                                <PosCustomIcon
                                                  icon={<CheckIcon />}
                                                  height={"16px"}
                                                  width={"20px"}
                                                  color="var(--blue-b0)"
                                                />
                                              )}
                                              {item?.name}
                                            </span>
                                          </ItemLabel>
                                          <span className="item-qty">x{item?.qty}</span>
                                          <ItemPreparation order={order} item={item} items={stationFilteredItems}
                                            completed={isConfirmedReady || item?.taskStatus === "COMPLETED" || item?.taskStatus === "REMOVED"}
                                            showPriority={false} inline />
                                          {rushMode &&
                                            (order?.status === "IN_PROGRESS" || order?.status === "ACCEPTED") &&
                                            item?.status === "IN_PROGRESS" &&
                                            item?.taskId != null &&
                                            item?.taskStatus !== "REMOVED" &&
                                            item?.taskStatus !== "COMPLETED" && (
                                              <button
                                                type="button"
                                                className={`kds-item-ready-button${isConfirmedReady ? " is-confirmed" : ""}`}
                                                aria-label={isConfirmedReady ? `Ready status for ${item?.name || "Item"}` : `Mark ${item?.name || "item"} ready`}
                                                aria-pressed={isConfirmedReady}
                                                aria-busy={isSavingItem}
                                                disabled={isConfirmedReady || !!buttonLoading[order?.id]}
                                                onClick={() => handleReady(order?.id, [item?.taskId])}
                                              >
                                                {isConfirmedReady ? (
                                                  <>
                                                    <CheckIcon aria-hidden="true" />
                                                    <span>Ready</span>
                                                  </>
                                                ) : isSavingItem ? (
                                                  <>
                                                    <span className="loader" aria-hidden="true" />
                                                    <span>Saving</span>
                                                  </>
                                                ) : (
                                                  <>
                                                    <CheckIcon aria-hidden="true" />
                                                    <span>Ready</span>
                                                  </>
                                                )}
                                              </button>
                                            )}
                                        </div>
                                          );
                                        })()}

                                        {/* Display notes, addons, and complimentary status */}
                                        {(item?.note ||
                                          (item?.addons && item?.addons.length > 0) ||
                                          item?.metadata?.isComplimentary ||
                                          item?.metadata?.isBundle) && (
                                          <div className="kds-item-tags-inline">
                                            {/* Display combo tag if bundle */}
                                            {item?.metadata?.isBundle && (
                                              <div className="kds-combo-pill">
                                                <span className="kds-combo-text">Combo</span>
                                              </div>
                                            )}
                                            {/* Display complimentary tag if present */}
                                            {item?.metadata?.isComplimentary && (
                                              <div className="kds-complimentary-pill">
                                                <GiftIcon className="kds-gift-icon" />
                                                <span className="kds-complimentary-text">
                                                  Complimentary
                                                </span>
                                              </div>
                                            )}

                                            {/* Display note if present */}
                                            {item?.note && (
                                              <div className="kds-note-pill">
                                                <svg
                                                  xmlns="http://www.w3.org/2000/svg"
                                                  width="14"
                                                  height="14"
                                                  viewBox="0 0 24 24"
                                                  fill="none"
                                                  stroke="#9A3C00"
                                                  strokeWidth="2"
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  className="lucide lucide-message-square-more h-3.5 w-3.5 flex-shrink-0"
                                                  aria-hidden="true"
                                                >
                                                  <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"></path>
                                                  <path d="M12 11h.01"></path>
                                                  <path d="M16 11h.01"></path>
                                                  <path d="M8 11h.01"></path>
                                                </svg>
                                                <span className="kds-note-text">{item?.note}</span>
                                              </div>
                                            )}

                                            {/* Display addons if present */}
                                            {item?.addons &&
                                              item?.addons?.length > 0 &&
                                              item?.addons?.map((addon, idx) => (
                                                <div className="kds-addon-pill" key={idx}>
                                                  <span className="kds-addon-plus">+</span>
                                                  <span className="kds-addon-text">
                                                    {addon?.itemName || addon}
                                                    {addon?.quantity &&
                                                      addon.quantity > 1 &&
                                                      ` x${addon.quantity}`}
                                                  </span>
                                                </div>
                                              ))}
                                          </div>
                                        )}

                                        {/* Dotted line separator */}
                                        <div className="item-separator"></div>
                                      </div>
                                    ))}
                                  </>
                                );
                              }

                              // If no in-progress items, just render items without select-all
                              return stationFilteredItems?.map(
                                (item, itemIndex) =>
                                  item?.taskStatus !== "COMPLETED" && (
                                    <div className="item-block" key={item?.taskId || item?.id}>
                                      <ServingGroupHeading order={order} item={item} items={stationFilteredItems} index={itemIndex} />
                                      <div
                                        className={`item-line ${
                                          item?.taskStatus === "REMOVED" ? "removed-item" : ""
                                        }`}
                                      >
                                        <label className="item-checkbox">
                                          <span className="item-name" title={item?.name || "Unnamed item"}>{item?.name || "Unnamed item"}</span>
                                        </label>
                                        <span className="item-qty">x{item?.qty}</span>
                                        <ItemPreparation order={order} item={item} items={stationFilteredItems}
                                          completed={item?.taskStatus === "REMOVED"} showPriority={false} inline />
                                      </div>

                                      {/* Display notes, addons, and complimentary status on item level */}
                                      {(item?.note ||
                                        (item?.addons && item?.addons?.length > 0) ||
                                        item?.metadata?.isComplimentary ||
                                        item?.metadata?.isBundle) && (
                                        <div className="kds-item-tags-inline">
                                          {/* Display combo tag if bundle */}
                                          {item?.metadata?.isBundle && (
                                            <div className="kds-combo-pill">
                                              <span className="kds-combo-text">Combo</span>
                                            </div>
                                          )}
                                          {/* Display complimentary tag if present */}
                                          {item?.metadata?.isComplimentary && (
                                            <div className="kds-complimentary-pill">
                                              <GiftIcon className="kds-gift-icon" />
                                              <span className="kds-complimentary-text">
                                                Complimentary
                                              </span>
                                            </div>
                                          )}

                                          {/* Display note if present */}
                                          {item?.note && (
                                            <div className="kds-note-pill">
                                              <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="#9A3C00"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="lucide lucide-message-square-more h-3.5 w-3.5 flex-shrink-0"
                                                aria-hidden="true"
                                              >
                                                <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"></path>
                                                <path d="M12 11h.01"></path>
                                                <path d="M16 11h.01"></path>
                                                <path d="M8 11h.01"></path>
                                              </svg>
                                              <span className="kds-note-text">{item?.note}</span>
                                            </div>
                                          )}

                                          {/* Display addons if present */}
                                          {item?.addons &&
                                            item?.addons?.length > 0 &&
                                            item?.addons?.map((addon, idx) => (
                                              <div className="kds-addon-pill" key={idx}>
                                                <span className="kds-addon-plus">+</span>
                                                <span className="kds-addon-text">
                                                  {addon?.itemName || addon}
                                                  {addon?.quantity &&
                                                    addon.quantity > 1 &&
                                                    ` x${addon?.quantity}`}
                                                </span>
                                              </div>
                                            ))}
                                        </div>
                                      )}

                                      {/* Dotted line separator */}
                                      <div className="item-separator"></div>
                                    </div>
                                  )
                              );
                            })()}
                          </div>

                          {order?.metadata?.comments && (
                            <div className="customerNotes">
                              <div className="kdsCustomerNotesUpperWrapper">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#F54900"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="lucide lucide-message-square-more h-3.5 w-3.5 flex-shrink-0"
                                  aria-hidden="true"
                                >
                                  <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"></path>
                                  <path d="M12 11h.01"></path>
                                  <path d="M16 11h.01"></path>
                                  <path d="M8 11h.01"></path>
                                </svg>
                                <p>COMMON COMMENT</p>
                              </div>
                              <p>{order?.metadata?.comments}</p>
                            </div>
                          )}

                          <div className="kds-order-footer">
                            {order?.status === "ACCEPTED" ? (
                              <button
                                onClick={() => handleProcessing(order?.id)}
                                className="start-btn"
                                disabled={buttonLoading[order?.id]}
                                aria-busy={!!buttonLoading[order?.id]}
                              >
                                {buttonLoading[order?.id] ? (
                                  <span className="loader"></span>
                                ) : (
                                  <>
                                    Start
                                    <ChevronRightIcon className="chevron-icon" />
                                  </>
                                )}
                              </button>
                            ) : order?.status === "IN_PROGRESS" ? (
                              <>
                                <button
                                  className={`reprint-btn ${
                                    order?.metadata?.cartStatus === "CLOSED"
                                      ? "reprint-btn-disabled"
                                      : ""
                                  } ${
                                    stationFilteredItems?.some(
                                      (item) => item?.metadata?.isComplimentary
                                    ) && !complimentaryKotPrinted[order?.id]
                                      ? "reprint-btn-complimentary"
                                      : ""
                                  }`}
                                  disabled={buttonLoading[order?.id] || kotPrintLoading[order?.id]}
                                  aria-busy={!!kotPrintLoading[order?.id]}
                                  onClick={() => {
                                    if (order?.metadata?.cartStatus === "CLOSED") {
                                      showSnackbar(
                                        "Unable to reprint the KOT as the cart status is closed",
                                        "warning"
                                      );
                                      return;
                                    }
                                    handlePrintKOT(order);
                                  }}
                                >
                                  {kotPrintLoading[order?.id] ? (
                                    <span className="loader"></span>
                                  ) : (
                                    <>
                                      Re-print KOT{" "}
                                      <PrinterIcon
                                        className="reprint-icon"
                                        style={{ width: "16px", height: "16px" }}
                                      />
                                    </>
                                  )}
                                </button>
                                {!rushMode && (
                                  <button
                                    onClick={() => handleReady(order?.id)}
                                    className={`ready-btn ${
                                      (selectedItems[order?.id]?.length || 0) === 0
                                        ? "disabled-btn"
                                        : ""
                                    }`}
                                    disabled={
                                      buttonLoading[order?.id] ||
                                      (selectedItems[order?.id]?.length || 0) === 0
                                    }
                                    aria-busy={buttonLoading[order?.id] === "ready"}
                                  >
                                    {buttonLoading[order?.id] === "ready" ? (
                                      <span className="loader"></span>
                                    ) : (
                                      <>
                                        Ready <CheckIcon className="ready-icon" />
                                      </>
                                    )}
                                  </button>
                                )}
                              </>
                            ) : null}
                          </div>
                        </TimedOrderCard>
                  ))}
                </div>
              );
            })()}

              </>
            )}
          </div>
        </div>

        <SideBarKDS
          handleCompletedOption={handleCompletedOption}
          handleFontChange={handleFontChange}
          fontSize={fontSize}
          handlePauseOrders={handlePauseOrders}
          handleRecalledItems={handleRecalledItems}
          handleRedoAccept={handleRedoAccept}
          handleOpenSettings={() => setOpenSettingsModal(true)}
          handleOpenMenuStock={handleOpenMenuStock}
          handleOpenInventoryStock={handleOpenInventoryStock}
        />
      </div>

      {selectedOrder && (
        <EditItem
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onSuccess={async () => {
            orderAlert.acknowledge([selectedOrder.id]);
            await refreshBothAfterAction();
          }}
        />
      )}
      <TableOrderDetails
        presentation={selectedTableKey ? "drawer" : "dialog"}
        open={detailsOpen && !selectedOrder && !workloadActions.commentTarget}
        table={detailTable}
        loading={itemWorkload.loading}
        error={itemWorkload.error}
        onRetry={itemWorkload.refresh}
        busy={workloadActions.busy || pendingActions.busy || tableActions.busy}
        actionBusy={pendingActions.busy || tableActions.busy}
        tablePendingActions={displayView === "tables" && !!selectedTableKey}
        activeAction={tableActions.action}
        allowAccept={approveOrders !== false}
        allowReject={allowRejectOrders}
        onAcceptOrders={displayView === "tables" && selectedTableKey ? tableActions.accept : pendingActions.accept}
        onRejectOrders={displayView === "tables" && selectedTableKey ? tableActions.reject : pendingActions.requestReject}
        onClose={() => { if (!tableActions.busy) { setSelectedTableKey(null); setDetailJobId(null); } }}
        onStart={workloadActions.onStart}
        onReady={workloadActions.onReady}
        onEdit={async (job) => {
          try {
            const current = job?.aggregated
              ? (await itemWorkload.getFreshJobs()).find((value) => String(value.jobId) === String(job.jobId))
              : job;
            if (!current) throw new Error("This kitchen order has changed. Please reopen its details.");
            setSelectedOrder(mapKitchenJob(current));
          } catch (error) {
            showSnackbar(error?.message || "Unable to open kitchen order details.", "error");
          }
        }}
        onComment={workloadActions.openComment}
        notes={workloadActions.notes}
        notesLoading={workloadActions.notesLoading}
        notesError={workloadActions.notesError}
        onReloadNotes={() => workloadActions.loadNotes(detailTable?.jobs || [])}
      />
      <Modal
        open={pendingActions.targetJobIds.length > 0}
        onClose={pendingActions.busy ? undefined : pendingActions.cancelReject}
        aria-labelledby="reject-confirm-title"
        aria-describedby="reject-confirm-warning"
        disableEscapeKeyDown={pendingActions.busy}
      >
        <Box className="kds-reject-modal">
          <RejectOrderConfirmation
            reasonOptions={pendingActions.reasonOptions}
            selectedReason={pendingActions.selectedReason}
            customReason={pendingActions.customReason}
            onReasonChange={pendingActions.setSelectedReason}
            onCustomReasonChange={pendingActions.setCustomReason}
            error={pendingActions.reasonError}
            isRejecting={pendingActions.busy}
            isReasonValid={pendingActions.isReasonValid}
            onCancel={pendingActions.cancelReject}
            onConfirm={pendingActions.confirmReject}
            title={pendingActions.targetJobIds.length > 1
              ? `Reject ${pendingActions.targetJobIds.length} orders?`
              : "Reject this order?"}
            warning="This decision applies only to the selected pending orders and cannot be undone."
          />
        </Box>
      </Modal>
      {workloadActions.commentTarget && (
        <PreparationCommentEditor
          key={workloadActions.commentTarget.product.cartProductId}
          target={workloadActions.commentTarget}
          busy={workloadActions.busy}
          error={workloadActions.commentError}
          onClose={workloadActions.closeComment}
          onSave={workloadActions.saveComment}
        />
      )}
      <KdsCompletedModal
        openCompletedModal={openCompletedModal}
        setOpenCompletedModal={setOpenCompletedModal}
        onClose={handleCompletedClose}
        onOrderReversed={handleOrderReversed}
      />

      <PauseOnlineOrdersModal open={openPauseOrders} onClose={handleClosePause} />
      <RecalledItemOrderModal
        open={openRecalledItems}
        setOpenRecalledItems={setOpenRecalledItems}
      />

      <RedoAcceptModal open={openRedoAccept} onClose={handleRedoAcceptClose} />
      <MenuStockModal open={openMenuStockModal} onClose={handleMenuStocksClose} />
      <InventoryStockModal open={openInventoryStockModal} onClose={handleMenuInventoryClose} />
      <KdsSettingsModal open={openSettingsModal} onClose={() => setOpenSettingsModal(false)} />
    </div>
    </KitchenClockProvider>
  );
};

// Guard the component as well as navigation so disabled KDS cannot mount through
// a direct URL or an initial Orders tab and start fetching or processing jobs.
const KitchenDisplaySystem = ({ onDisabled, ...props }) => {
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const isStandalone = pathname === "/products/kitchen-display";
  const availability = useKdsAvailability(isStandalone);
  const showSnackbar = useSnackbar();
  const hasWarned = useRef(false);
  const appConfig = JSON.parse(localStorage.getItem("appConfig")) || {};
  const isKdsEnabled = isStandalone
    ? availability.enabled
    : !!appConfig?.settings?.DISPLAY_SYSTEM_MODE?.kdsDisplayScreens?.includes("KDS");

  useEffect(() => {
    if (isStandalone && availability.loading) return;
    if (isKdsEnabled) {
      hasWarned.current = false;
      return;
    }

    if (!hasWarned.current) {
      hasWarned.current = true;
      showSnackbar(availability.error && isStandalone ? "Unable to prepare KDS" : "KDS is disabled from the RMS", "warning");
      if (onDisabled) {
        onDisabled();
      } else {
        if (isStandalone) {
          navigate("/products/access-mode", { replace: true });
        } else {
          navigate("/orders", { replace: true, state: { initialTab: "Order History" } });
        }
      }
    }
  }, [isKdsEnabled, isStandalone, availability.loading, availability.error, navigate, showSnackbar, onDisabled]);

  const screen = isStandalone && availability.loading
    ? <div role="status"><CircularProgress size={28} /> Preparing KDS</div>
    : isKdsEnabled ? <KitchenDisplayScreen {...props} /> : null;

  return isStandalone && state?.fromAccessMode
    ? <AccessModeKdsNavbar>{screen}</AccessModeKdsNavbar>
    : screen;
};

export default KitchenDisplaySystem;
